import styled from "@emotion/styled"
import { PButton } from "@porsche-design-system/components-react"
import type { JSX, PropsWithChildren } from "react"
import { useCallback, useRef, useState } from "react"
import { FlyoutPortal } from "./FlyoutPortal"

export interface HoverableFlyoutProps extends PropsWithChildren {
	/**
	 * The alignment of the flyout
	 * default: left
	 */
	align?: "left" | "right"

	/**
	 * The direction in which the flyout opens relative to the triggerElement.
	 * "auto" resolves to "Up" if the triggerElement is positioned in the lower third or the screen, otherwise it resolves to "Down".
	 * default: auto
	 */
	direction?: "auto" | "up" | "down"

	/**
	 * Set this to true if the triggerElement is at a fixed position
	 * default: false
	 */
	fixed?: boolean

	/**
	 * Displays the button label
	 */
	label?: string

	/**
	 *  The Element that is hoverable.
	 */
	trigger?: JSX.Element

	width?: "narrow" | "default" | "wide"
}

export const HoverableFlyout = (props: HoverableFlyoutProps) => {
	const { align, children, direction, fixed, label, trigger, width, ...rest } = props

	const [isOpen, setIsOpen] = useState(false)
	const [openDirection, setOpenDirection] = useState<"up" | "down">("down")
	const [triggerBounds, setTriggerBounds] = useState<DOMRect | undefined>()
	const triggerRef = useRef<HTMLDivElement>(null)

	const openFlyout = useCallback(() => {
		const currentDirection = getOpenDirection(direction, triggerRef.current)
		const currentBounds = triggerRef.current?.getBoundingClientRect()
		setOpenDirection(currentDirection)
		setTriggerBounds(currentBounds)
		setIsOpen(true)
	}, [direction])

	const closeFlyout = useCallback(() => {
		setIsOpen(false)
	}, [setIsOpen])

	return (
		<Wrapper {...rest}>
			<div onMouseEnter={openFlyout} onMouseLeave={closeFlyout} ref={triggerRef}>
				{trigger ?? (
					<PButton type="button" variant="secondary" icon="arrow-head-down">
						{label}
					</PButton>
				)}
			</div>
			{isOpen && triggerBounds && (
				<FlyoutPortal
					align={align}
					width={width}
					fixed={fixed}
					direction={openDirection}
					onMouseEnter={openFlyout}
					onMouseLeave={closeFlyout}
					triggerBounds={triggerBounds}
				>
					{children}
				</FlyoutPortal>
			)}
		</Wrapper>
	)
}

const Wrapper = styled.div`
	position: relative;
	display: block;
	width: 100%;
`

function getOpenDirection(direction: HoverableFlyoutProps["direction"], trigger: HTMLDivElement | null) {
	if (direction === "up" || direction === "down") {
		return direction
	}
	// Direction is "auto"
	return trigger && trigger.getBoundingClientRect().top > (window.innerHeight * 2) / 3 ? "up" : "down"
}
